/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import { MdOutlinePauseCircleFilled, MdOutlinePlayCircleFilled } from "react-icons/md";
import { HeadersFunction, LoaderFunction, MetaFunction, useLoaderData, useParams, useSearchParams } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import Chip from "~/components/shared-components/Chip";
import CircularAudioProgress from "~/components/shared-components/CircularAudioProgress";
import ClientOnly from "~/components/shared-components/ClentOnly";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
import { useFontStyle } from "~/hook/useFontStyle";
import { Loaderdata } from "~/interface/ringtone/ringtone";
import { useAnalytics } from "~/lib/firebase/analytics";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { touchVariants } from "~/lib/motion-obj";
import { changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import API_PATH from "~/utils/api-routes";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG, PLAY_PAUSE_ICON_COLOR } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import api from "~/utils/Interceptor";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export const loader: LoaderFunction = async ({ request, params }) => {
  const lang = params.lang;
  const url = new URL(request.url);
  let subCategories: any = null;
  const mediaType = "ringtone_cat";

  //fetch master data
  const apiService = new ApiService(lang || LANG.english);
  const masterdataResponce = await apiService.getMasterData();
  const mediaCategoriesArray = masterdataResponce[mediaType] || [];
  subCategories = url.searchParams.get("categories") ? url.searchParams.get("categories") : null;

  // Extract the ids of the selected categories
  const subCategoriesId =
    subCategories !== null &&
    mediaCategoriesArray.filter((item: any) => {
      const matchedCategory = item.text === subCategories;
      return matchedCategory ? item.id : null;
    });

  //fetch ringtine data
  let responce;
  try {
    responce = await api.get(`${API_PATH.SATSANG_MEDIA_RING_TONE}`, {
      params: {
        lang: params.lang,
        subCategory: subCategoriesId ? subCategoriesId[0].id : null,
      },
    });
  } catch (error) {
    console.error("Error fetching ringtone data:", error);
    return Response.json({
      ringTone: [],
      title: "Ringtone",
      mediaCategoriesArray,
      subCategories,
    });
  }

  const metaTitle =
    lang === LANG.gujarati
      ? "સ્વામિનારાયણ રીંગટોન – ધૂન, કીર્તન, ઇન્સ્ટ્રુમેન્ટલ અને બાળ રીંગટોન | ફ્રી ડાઉનલોડ | SwaminarayanBhagwan.org"
      : "Swaminarayan Ringtones – Dhun, Kirtan, Instrumental & Children Ringtones | Free Download | SwaminarayanBhagwan.org";

  const metaDescription =
    lang === LANG.gujarati
      ? "શ્રી સ્વામિનારાયણ મંદિર, કુંડળધામ દ્વારા ભક્તિ રીંગટોન ફ્રી ડાઉનલોડ કરો — ધૂન રીંગટોન, ઇન્સ્ટ્રુમેન્ટલ રીંગટોન, બાળ રીંગટોન અને જનરલ રીંગટોન. સ્વામિનારાયણ મહામંત્રનો દિવ્ય સ્વર તમારા ફોનમાં સેટ કરો."
      : "Download free Swaminarayan devotional ringtones — Dhun ringtones, Instrumental ringtones, Children's ringtones & General ringtones from Shree Swaminarayan Temple, Kundaldham. Set the divine sound of Swaminarayan Mahamantra as your phone ringtone today.";

  const title = subCategories
    ? `${subCategories} ${lang === LANG.gujarati ? "સ્વામિનારાયણ રિંગટોન" : "Swaminarayan Ringtone"}`
    : lang === LANG.gujarati
      ? "સ્વામિનારાયણ રિંગટોન"
      : "Swaminarayan Ringtone";

  if (responce.status === 200) {
    const data = responce.data.data.rows;
    const ringTone = data;

    const DATA = {
      ringTone,
      title,
      mediaCategoriesArray,
      subCategories,
      metaTitle,
      metaDescription,
    };

    const etag = computeEtagFromData(DATA);

    const ifNoneMatch = request.headers.get("If-None-Match");
    if (ifNoneMatch === etag) {
      return new Response(null, {
        status: 304,
        headers: {
          Etag: etag,
        },
      });
    }

    return Response.json(DATA, {
      headers: {
        ...CACHED_PAGE.SATSANG_MEDIA.HEADERS,
        ETag: etag,
      },
    });
  } else {
    return Response.json({
      ringTone: [],
      title,
      mediaCategoriesArray,
      subCategories,
      metaTitle,
      metaDescription,
    });
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const meta: MetaFunction = ({ location, data, params }) => {
  const pathname = location?.pathname || "/";
  const search = location?.search || "";
  const ringtone = data as Loaderdata;
  const lang = params.lang || "en";
  const meta = getPageMeta("satsangMedia", lang);
  return metaService({
    title: `${ringtone.metaTitle}` || meta.metaTitle,
    description: `${ringtone.metaDescription}` || meta.metaDescription,
    ogTitle: `${ringtone.metaTitle}` || meta.ogTitle,
    ogDescription: `${ringtone.metaDescription}` || meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname + search,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: "Satsang Media", path: "/satsang-media" },
    { name: match.data?.title || "Ringtone", path: "/satsang-media/ring-tone" },
  ],
};

const Ringtone = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const lang = useParams().lang || LANG.english;
  const { ringTone, mediaCategoriesArray, subCategories } = useLoaderData<Loaderdata>();
  const { medium } = useFontStyle();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackDownload } = useAnalytics();

  const { isPlaying } = useAppSelector((state) => state.nityNiyam);

  const [playingAudioId, setPlayingAudioId] = useState<number | null>(null);
  const [currentProgress, setCurrentProgress] = useState<{
    [key: number]: number;
  }>({});
  const [currentDuration, setCurrentDuration] = useState<{
    [key: number]: string;
  }>({});
  const [totalDuration, setTotalDuration] = useState<{ [key: number]: string }>({});

  const handleApplyFilter = (value: string | number) => {
    if (value === subCategories) {
      setSearchParams({}, { replace: true });
      return;
    } else {
      setSearchParams({ categories: value.toString() }, { replace: true });
    }
  };

  const handlePlayPause = (id: number, audioElement: HTMLAudioElement) => {
    if (playingAudioId === id) {
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      if (playingAudioId !== null) {
        const currentlyPlaying = document.getElementById(`audio-${playingAudioId}`) as HTMLAudioElement;
        currentlyPlaying.pause();
        currentlyPlaying.currentTime = 0;
        setCurrentProgress((prev) => ({
          ...prev,
          [playingAudioId]: 0,
        }));
        setCurrentDuration((prev) => ({
          ...prev,
          [playingAudioId]: "00:00",
        }));
      }
      audioElement.play();
      setPlayingAudioId(id);
      setTotalDuration((prev) => ({
        ...prev,
        [id]: formatTime(audioElement.duration),
      }));
    }
    dispatch(changeIsPlayingState(false));
  };

  const handleTimeUpdate = (id: number, audioElement: HTMLAudioElement) => {
    const progress = (audioElement.currentTime / audioElement.duration) * 100;
    const time = formatTime(audioElement.currentTime);

    setCurrentProgress((prev) => ({
      ...prev,
      [id]: progress,
    }));
    setCurrentDuration((prev) => ({
      ...prev,
      [id]: time,
    }));
  };

  const handleAudioEnded = (id: number) => {
    setPlayingAudioId(null);
    setCurrentProgress((prev) => ({
      ...prev,
      [id]: 0,
    }));
    setCurrentDuration((prev) => ({
      ...prev,
      [id]: "00:00",
    }));
    const audioElement = document.getElementById(`audio-${id}`) as HTMLAudioElement;
    audioElement.currentTime = 0;
  };

  const handleSeek = (id: number, newProgress: number) => {
    const audioElement = document.getElementById(`audio-${id}`) as HTMLAudioElement;
    const newTime = (newProgress / 100) * audioElement.duration;
    audioElement.currentTime = newTime;
    setCurrentProgress((prev) => ({
      ...prev,
      [id]: newProgress,
    }));
    setCurrentDuration((prev) => ({
      ...prev,
      [id]: formatTime(newTime),
    }));
  };

  const handleDownload = (url: string, name: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "ringtone.mp3";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    trackDownload(`${name}.mp3`, "audio", "SatsangMediaRingTonePage");
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || time === 0) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Helper function to ensure duration is in mm:ss format
  const formatDuration = (duration: string | number | null | undefined): string => {
    if (!duration) return "00:00";

    // If duration is already a string in mm:ss format, return it
    if (typeof duration === "string" && duration.includes(":")) {
      return duration;
    }

    // If duration is a number (seconds), format it
    if (typeof duration === "number") {
      return formatTime(duration);
    }

    // If duration is a string number (seconds as string), convert and format
    const durationInSeconds = parseFloat(duration);
    if (!isNaN(durationInSeconds)) {
      return formatTime(durationInSeconds);
    }

    return "00:00";
  };

  useEffect(() => {
    if (isPlaying && playingAudioId !== null) {
      const audioElement = document.getElementById(`audio-${playingAudioId}`) as HTMLAudioElement;
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
        setPlayingAudioId(null);
        setCurrentProgress((prev) => ({
          ...prev,
          [playingAudioId]: 0,
        }));
        setCurrentDuration((prev) => ({
          ...prev,
          [playingAudioId]: "00:00",
        }));
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    const audioElements = document.querySelectorAll("audio");
    audioElements.forEach((audioElement) => {
      audioElement.addEventListener("loadedmetadata", () => {
        const duration = formatTime(audioElement.duration);

        setTotalDuration((prev) => ({
          ...prev,
          [parseInt(audioElement.id.split("-")[1])]: duration,
        }));
      });
    });

    return () => {
      audioElements.forEach((audioElement) => {
        audioElement.removeEventListener("loadedmetadata", () => {});
      });
    };
  }, [ringTone]);

  useEffect(() => {
    setPlayingAudioId(null);
    setCurrentProgress({});
    setCurrentDuration({});
    setTotalDuration({});
  }, [subCategories]);

  return (
    <div className="w-full flex flex-col justify-start items-center gap-4 mb-5 px-3 md:px-0">
      <Chip chipData={mediaCategoriesArray?.map((item) => item.text)} selectedValue={subCategories} onClick={handleApplyFilter} />
      <ClientOnly
        fallback={
          <div className="grid w-full body-container grid-cols-1 gap-4 lg:grid-cols-2">
            {ringTone?.map((item) => (
              <div key={item.id} className="bg-cardBgColor flex flex-col p-3 rounded-xl relative">
                <div className="flex items-center space-x-4">
                  <div className="relative flex items-center justify-center h-[50px] w-[50px] cursor-not-allowed">
                    <div className="flex items-center justify-center h-[40px] w-[40px] bg-[#F0F0F0] rounded-full z-10">
                      <MdOutlinePlayCircleFilled size={30} color={PLAY_PAUSE_ICON_COLOR} />
                    </div>
                  </div>
                  <div className={cn("w-[calc(100%-120px)] flex flex-col justify-start items-start")}>
                    <div className={cn("w-full overflow-hidden")}>
                      <p
                        className={cn("text-[14px] font-normal w-full", "text-textColor line-clamp-2", medium)}
                        title={lang === LANG.gujarati ? item.gu_name || "No Title Available" : item.name || "No Title Available"}
                      >
                        {lang === LANG.gujarati ? item.gu_name || "No Title Available" : item.name || "No Title Available"}
                      </p>
                    </div>
                    <p className={cn("text-14 text-textColor font-medium font-robotomono")}>
                      00:00 / {formatDuration(item.audio_duration) || "00:00"}
                    </p>
                  </div>
                  <div className="absolute right-4 bg-white rounded-full size-[44px] flex justify-center items-center cursor-not-allowed">
                    <FiDownload size={21} color={PLAY_PAUSE_ICON_COLOR} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 w-full body-container lg:grid-cols-2">
          {ringTone?.map((item, index) => {
            return (
              <div key={item.id} className="bg-cardBgColor flex flex-col p-3 rounded-xl relative">
                <div className="flex items-center space-x-4">
                  <div
                    className="relative flex items-center justify-center h-[50px] w-[50px] cursor-pointer"
                    onClick={() => {
                      const audioElement = document.getElementById(`audio-${item.id}`) as HTMLAudioElement;
                      handlePlayPause(item.id, audioElement);
                    }}
                    title={playingAudioId === item.id ? "Pause" : "Play"}
                  >
                    <CircularAudioProgress offset={(currentProgress[item.id] || 0) === 0 ? 100 : 100 - currentProgress[item.id]} />
                    <div className="flex items-center justify-center h-[40px] w-[40px] bg-[#F0F0F0] rounded-full z-10">
                      {playingAudioId === item.id ? (
                        <MdOutlinePauseCircleFilled size={30} color={PLAY_PAUSE_ICON_COLOR} />
                      ) : (
                        <MdOutlinePlayCircleFilled size={30} color={PLAY_PAUSE_ICON_COLOR} />
                      )}
                    </div>
                  </div>

                  <div className={cn("w-[calc(100%-120px)] flex flex-col justify-start items-start")}>
                    <div className={cn("w-full overflow-hidden", playingAudioId === item.id ? "h-auto" : "")}>
                      <motion.p
                        // animate={playingAudioId === item.id ? { x: ["0%", "-100%"] } : { x: "0%" }}
                        // key={playingAudioId === item.id ? item.id : "stopped"}
                        // transition={{
                        //   duration: 10,
                        //   ease: "linear",
                        //   repeat: Infinity,
                        //   repeatType: "loop",
                        // }}
                        className={cn(
                          "text-[14px] font-normal w-full",
                          playingAudioId === item.id ? "text-textHeadingColor" : "text-textColor line-clamp-2",
                          medium,
                        )}
                        title={lang === LANG.gujarati ? item.gu_name || "No Title Available" : item.name || "No Title Available"}
                      >
                        {lang === LANG.gujarati ? item.gu_name || "No Title Available" : item.name || "No Title Available"}
                      </motion.p>
                    </div>
                    <p
                      className={cn("text-14 text-textColor font-medium font-robotomono", playingAudioId === item.id ? "opacity-100" : "opacity-50")}
                    >
                      {currentDuration[item.id] || "00:00"} /{" "}
                      {formatDuration(item.audio_duration) || totalDuration[item.id] || formatTime(audioElement?.duration) || "00:00"}
                    </p>
                  </div>

                  <div className="absolute right-4 bg-white rounded-full size-[44px] flex justify-center items-center cursor-pointer">
                    <motion.button
                      initial={"initial"}
                      whileTap={"tap"}
                      variants={touchVariants}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownload(item.media_url, item.name);
                      }}
                      title="Download"
                    >
                      <FiDownload size={21} color={PLAY_PAUSE_ICON_COLOR} />
                    </motion.button>
                  </div>
                </div>

                <audio
                  id={`audio-${item.id}`}
                  src={item.media_url}
                  onTimeUpdate={(e) => handleTimeUpdate(item.id, e.target as HTMLAudioElement)}
                  onEnded={() => handleAudioEnded(item.id)}
                />
                <input
                  type="range"
                  className="w-full mt-2 hidden"
                  min={0}
                  max={100}
                  step={0.1}
                  value={currentProgress[item.id] || 0}
                  onChange={(e) => handleSeek(item.id, parseFloat(e.target.value))}
                />
              </div>
            );
          })}
        </div>
      </ClientOnly>

      <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData")} />
    </div>
  );
};

export default Ringtone;
